import { createUseStyles } from "react-jss";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useQuery } from "react-query";
import { theme } from "$utils/theme/bataillog";
import { DeliveryType, DispositionType, ShippingRecord } from "$types";
import {
  getShippingLocationLabelFromAddress,
  getDeliveryLocationLabelFromAddress,
  removeDuplicateDeliveryAddresses,
} from "$utils/typeConstants";
import InfoContainer from "$components/InfoContainer";
import Text from "$components/Text";
import { OpService } from "$services";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  classes: any;
  shippingRecord: ShippingRecord;
};

function ShippingLocationInfoBlock({ shippingRecord, classes }: Props) {
  const firstShipmentOpId = shippingRecord?.shipments?.[0]?.opId;

  const { data: op, isLoading } = useQuery(
    [CacheInvalidationAction.GET_OP_BY_ID, firstShipmentOpId],
    () => OpService.getPublicOpById(firstShipmentOpId),
    { cacheTime: 60 * 5 * 3000, enabled: !!firstShipmentOpId }
  );

  const localClasses = useStyles();

  return (
    <InfoContainer
      className={classes.shippingLocationInfoPaper}
      leftHeader={
        <div className={classes.infoHeader}>
          <LocalShippingOutlinedIcon />
          <Text type="h3">Informations livraison</Text>
        </div>
      }
      isLoading={isLoading}
    >
      <div className={localClasses.mainContainer}>
        <div>
          <Text type="text" className={classes.subTitle}>
            {`Lieu d'enlèvement : `}
          </Text>
          <Text type="important">
            {shippingRecord
              ? getShippingLocationLabelFromAddress(shippingRecord)
              : ""}
          </Text>
        </div>
        <div className={localClasses.deliveryAddressesContainer}>
          <div>
            <Text type="text" className={classes.subTitle}>
              {`Lieu(x) de livraison : `}
            </Text>
          </div>
          <div className={localClasses.deliveryAddressesList}>
            {shippingRecord?.deliveryType === DeliveryType.PROVISIONING ? (
              <Text type="important">
                {shippingRecord
                  ? // Demande métier: afficher l'adresse d'enlèvement ici dans le cas d'une mise à disposition
                    getShippingLocationLabelFromAddress(shippingRecord)
                  : ""}
              </Text>
            ) : (
              removeDuplicateDeliveryAddresses(
                shippingRecord?.shipments?.map((shipment) => {
                  if (shipment?.toAddress.name === "") {
                    if (op?.data.shipType === DispositionType.OTHER_PLACE) {
                      // eslint-disable-next-line no-param-reassign
                      shipment.toAddress.name = op.data.shipPlace;
                    }
                    if (op?.data.shipType === DispositionType.ON_DOCKYARD) {
                      // eslint-disable-next-line no-param-reassign
                      shipment.toAddress.name = `${op.data.chantier.code} ${op.data.chantier.name}`;
                    }
                  }
                  return shipment.toAddress;
                })
              )?.map((adress, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Text type="important" key={`adress-${index}`}>
                  {getDeliveryLocationLabelFromAddress(adress)}
                </Text>
              ))
            )}
          </div>
        </div>
      </div>
    </InfoContainer>
  );
}

export default ShippingLocationInfoBlock;

const useStyles = createUseStyles(() => ({
  mainContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  deliveryAddressesContainer: {
    display: "flex",
    alignItems: "baseline",
  },
  deliveryAddressesList: {
    display: "flex",
    flexDirection: "column",
    marginLeft: theme.spacing(1),
  },
}));
